import {
	type AlertContext,
	type Alert as AppSyncAlert,
	type WeatherHazardAlert as AppSyncWeatherHazardAlert,
	type ArchiveAlertResponse,
	type CapSeverity,
	CapSeveritySchema,
} from '@xweather/protect-types'
import { groupBy } from 'es-toolkit'
import { maxBy } from 'es-toolkit/compat'
import { dayjs } from '../../dayjs.ts'
import type { Severity, WeatherHazardAlert } from './types.ts'

const getERGECMWFHRESSeverity = (): Severity | undefined => 'warning'
const getHailThreatSeverity = (): Severity | undefined => 'warning'
const getWeatherHazardSeverity = (weatherHazardSeverity?: string): Severity | undefined =>
	weatherHazardSeverity ? capSeverityToSeverity[parseCapSeverity(weatherHazardSeverity)] : undefined
const getXweatherForecastsSeverity = (): Severity | undefined => 'warning'

const severityHandlers: Record<string, (args?: string) => Severity | undefined> = {
	ERG_ECMWF_HRES: getERGECMWFHRESSeverity,
	HAIL_THREAT: getHailThreatSeverity,
	WEATHER_HAZARD: getWeatherHazardSeverity,
	XWEATHER_FORECASTS: getXweatherForecastsSeverity,
}

const getSeverityFromSource = (
	source: string | undefined | null,
	severityValueExtractor: () => string | undefined,
): Severity | undefined => {
	if (!source) return undefined
	const handler = severityHandlers[source]
	return handler?.(severityValueExtractor())
}

export const alertContextToSeverity = (ctx: AppSyncAlert['alertContext'][number]) => {
	return getSeverityFromSource(ctx.source, () =>
		ctx.source === 'WEATHER_HAZARD' ? ctx.alertContextValue.WEATHER_HAZARD_SEVERITY : undefined,
	)
}

export const alertArchiveToSeverity = (e: ArchiveAlertResponse) => {
	return getSeverityFromSource(e.source, () => (e.source === 'WEATHER_HAZARD' ? e.context?.WEATHER_HAZARD_SEVERITY : undefined))
}

export const severityAsNumberMap: Record<Severity, number> = {
	alarm: 3,
	warning: 2,
	info: 1,
}

export const capSeverityToSeverity: Record<CapSeverity, Severity | undefined> = {
	EXTREME: 'alarm',
	SEVERE: 'alarm',
	MODERATE: 'warning',
	MINOR: 'info',
	UNKNOWN: undefined,
}
export const parseCapSeverity = (capSeverity: string): CapSeverity => {
	const parsed = CapSeveritySchema.safeParse(capSeverity)
	return parsed.success ? parsed.data : 'UNKNOWN'
}

// Deduplicate WeatherHazardAlert contexts by key fields, picking highest severity in group
export const deduplicateWeatherHazardContexts = (
	contexts: AlertContext<AppSyncWeatherHazardAlert>[],
): WeatherHazardAlert['context'][number][] => {
	const consideredFields = [
		'WEATHER_HAZARD_TYPE',
		'WEATHER_HAZARD_BEGIN_AT',
		'WEATHER_HAZARD_EXPIRE_AT',
		'WEATHER_HAZARD_TITLE',
		'WEATHER_HAZARD_BODY',
	] as const
	const capSeverityRank: Record<string, number> = {
		EXTREME: 4,
		SEVERE: 3,
		MODERATE: 2,
		MINOR: 1,
	}
	return Object.values(groupBy(contexts, (ctx) => JSON.stringify(consideredFields.map((f) => ctx.alertContextValue[f] ?? '')))).map(
		(group) => {
			// biome-ignore lint/style/noNonNullAssertion: groupBy shouldn't return empty groups
			const first = group[0]!
			const highestSeverityContext = maxBy(group, (ctx) => capSeverityRank[ctx.alertContextValue.WEATHER_HAZARD_SEVERITY] ?? 0) ?? first
			const severity = highestSeverityContext.alertContextValue.WEATHER_HAZARD_SEVERITY
			const isChanged = group.every((ctx) => ctx.changed)
			const issuedAt =
				maxBy(group, (ctx) => dayjs(ctx.alertContextValue.WEATHER_HAZARD_ISSUED_AT).valueOf())?.alertContextValue
					.WEATHER_HAZARD_ISSUED_AT ?? first.alertContextValue.WEATHER_HAZARD_ISSUED_AT
			return {
				...first,
				changed: isChanged,
				alertContextValue: {
					...first.alertContextValue,
					WEATHER_HAZARD_SEVERITY: severity,
					WEATHER_HAZARD_ISSUED_AT: issuedAt,
				},
			}
		},
	)
}
